/**
 * Export Utilities
 * Sprint 4 - CSV/Excel export functionality
 */

/**
 * Column definition for export
 */
export interface ExportColumn<T> {
  key: keyof T | string;
  header: string;
  formatter?: (value: unknown, row: T) => string;
}

/**
 * Export options
 */
export interface ExportOptions {
  filename: string;
  format: 'csv' | 'xlsx';
  delimiter?: string;
  includeHeaders?: boolean;
  dateFormat?: string;
}

/**
 * Format date for export
 */
export function formatDateForExport(date: string | Date | null | undefined, format?: string): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';

  // Default format: DD/MM/YYYY
  if (!format || format === 'DD/MM/YYYY') {
    return d.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  // ISO format
  if (format === 'ISO') {
    return d.toISOString().split('T')[0];
  }

  return d.toLocaleDateString('fr-FR');
}

/**
 * Format currency for export
 */
export function formatCurrencyForExport(value: number | null | undefined, currency = 'EUR'): string {
  if (value === null || value === undefined) return '';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Format number for export (no currency symbol, uses comma as decimal separator)
 */
export function formatNumberForExport(value: number | null | undefined): string {
  if (value === null || value === undefined) return '';
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Escape CSV value (handle quotes and commas)
 */
function escapeCSVValue(value: string): string {
  if (value.includes('"') || value.includes(',') || value.includes('\n') || value.includes(';')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Get nested value from object using dot notation
 */
function getNestedValue<T>(obj: T, path: string): unknown {
  return path.split('.').reduce((acc: unknown, part) => {
    if (acc && typeof acc === 'object' && part in acc) {
      return (acc as Record<string, unknown>)[part];
    }
    return undefined;
  }, obj);
}

/**
 * Convert data to CSV format
 */
export function toCSV<T>(
  data: T[],
  columns: ExportColumn<T>[],
  options: Partial<ExportOptions> = {}
): string {
  const delimiter = options.delimiter || ';'; // French Excel uses semicolon
  const includeHeaders = options.includeHeaders !== false;

  const lines: string[] = [];

  // Add headers
  if (includeHeaders) {
    const headerLine = columns.map(col => escapeCSVValue(col.header)).join(delimiter);
    lines.push(headerLine);
  }

  // Add data rows
  for (const row of data) {
    const values = columns.map(col => {
      const rawValue = getNestedValue(row, col.key as string);

      if (col.formatter) {
        return escapeCSVValue(col.formatter(rawValue, row));
      }

      if (rawValue === null || rawValue === undefined) {
        return '';
      }

      if (typeof rawValue === 'number') {
        return formatNumberForExport(rawValue);
      }

      if (rawValue instanceof Date) {
        return formatDateForExport(rawValue);
      }

      return escapeCSVValue(String(rawValue));
    });

    lines.push(values.join(delimiter));
  }

  return lines.join('\n');
}

/**
 * Download data as CSV file
 */
export function downloadCSV<T>(
  data: T[],
  columns: ExportColumn<T>[],
  options: ExportOptions
): void {
  const csv = toCSV(data, columns, options);

  // Add BOM for UTF-8 (helps Excel recognize encoding)
  const bom = '\uFEFF';
  const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' });

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', `${options.filename}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Download data as Excel file (XLSX)
 * Note: For true XLSX format, you'd need a library like SheetJS
 * This creates a CSV that Excel can open with proper formatting
 */
export function downloadExcel<T>(
  data: T[],
  columns: ExportColumn<T>[],
  options: ExportOptions
): void {
  // For now, we use CSV with Excel-compatible formatting
  // True XLSX would require adding SheetJS dependency
  downloadCSV(data, columns, {
    ...options,
    filename: options.filename,
    delimiter: ';', // Excel FR uses semicolon
  });
}

/**
 * Generic export function
 */
export function exportData<T>(
  data: T[],
  columns: ExportColumn<T>[],
  options: ExportOptions
): void {
  if (options.format === 'xlsx') {
    downloadExcel(data, columns, options);
  } else {
    downloadCSV(data, columns, options);
  }
}

/**
 * Fetch all pages of paginated data sequentially
 * Iterates page by page to avoid overloading the database
 */
export async function fetchAllPages<T>(
  fetchFn: (page: number) => Promise<{ data: T[]; totalPages: number }>
): Promise<T[]> {
  const allData: T[] = [];
  let currentPage = 1;
  let totalPages = 1;

  do {
    const result = await fetchFn(currentPage);
    allData.push(...result.data);
    totalPages = result.totalPages;
    currentPage++;
  } while (currentPage <= totalPages);

  return allData;
}
